import { AnalyteConfig, SeriesConfig, BasicCartesianChartConfig } from '@app/data/interface/widget-config';
import {
  BasicCartesianChartEchartConfigTemplate,
  BasicCartesianChartAxisTemplate,
} from '@app/data/widget-config-templates/basic-cartesian-chart';

const analytesWai = [
  {
    analyte: 'dissolved-oxygen-in-field',
    name: 'Dissolved Oxygen',
    unit: 'g/m³',
    default: true,
    color: '#8c7ebb',
  } as AnalyteConfig,
  {
    analyte: 'water-temperature',
    name: 'Water Temp',
    unit: 'C',
    default: true,
    color: '#00b1d8',
  } as AnalyteConfig,
];

const analytesWaterLevelAndTemp = [
  {
    analyte: 'water-temperature',
    name: 'Water Temp',
    unit: 'C',
    default: true,
    color: '#00b1d8',
  } as AnalyteConfig,
  {
    analyte: 'relative-water-level-mm',
    name: 'Water Level',
    unit: 'mm',
    default: true,
    color: '#00b1d8',
  } as AnalyteConfig,
];

//
// generate a series (combination of analyte names and environmental datasources)
//
function generateSeries(
  dataSourceIds: string[],
  analytes: AnalyteConfig[],
  options: any = { extendedName: false }
): SeriesConfig[] {
  return dataSourceIds.reduce((acc, dsId) => {
    return acc.concat(
      analytes.map(
        (entry) =>
          ({
            ...entry,
            dataSourceIdentitfier: dsId,
            name: dataSourceIds.length > 1 || options.extendedName ? `${entry.name} (${dsId})` : entry.name,
          } as SeriesConfig)
      ) as []
    );
  }, []);
}

//
// configure a chart with a series
// note: this needs to be consolidated with other versions (e.g. Raukawa)
//
function ConfigureChart(title: string, series: SeriesConfig[]): BasicCartesianChartConfig {
  // group analytes by unit
  const analytesByUnit = series.reduce((acc, curr) => {
    const unit = curr.unit || 'no unit';
    const existingUnit = acc.find((item) => item.unit === unit);
    if (existingUnit) {
      existingUnit.analytes.push(curr);
    } else {
      acc.push({ unit, analytes: [curr] });
    }
    return acc;
  }, [] as Array<{ unit: string; analytes: (typeof series)[0][] }>);

  return {
    echartsConfig: {
      ...BasicCartesianChartEchartConfigTemplate,
      color: series.map((a) => (a.color ? a.color : undefined)).filter((c) => c !== undefined) as string[],
      grid: {
        top: '30%',
        left: '11%',
        right: '11%',
        bottom: '6%',
        containLabel: true,
      },
      legend: {
        padding: [10, 10, 10, 10], // top, right, bottom, left
        show: true,
        data: series.map((aa) => aa.name),
        selected: series.reduce((acc, a) => {
          acc[a.name] = a.default;
          return acc;
        }, {} as Record<string, boolean>),
      },
      yAxis: analytesByUnit.map((entry, index) => {
        return {
          ...BasicCartesianChartAxisTemplate,
          type: 'value',
          position: index % 2 === 0 ? 'left' : 'right',
          offset: 40 * (index / 2),
          name: entry.unit,
          axisLine: {
            show: true,
            lineStyle: {
              color: entry.analytes[0].color,
            },
          },
          axisLabel: {
            color: entry.analytes[0].color,
          },
        };
      }),
      series: analytesByUnit.reduce((acc, curr, i) => {
        const s = curr.analytes.map((entry, index) => ({
          type: 'line',
          name: entry.name,
          smooth: true,
          symbolSize: 13,
          yAxisIndex: i,
          showSymbol: true,
          connectNulls: true,
        })) as [];
        return [...acc, ...s];
      }, []),
    },
    dataConfig: {
      title: title,
      widgetId: 1,
      domain: 'wai',
      dataSources: series.map((entry) => ({
        environmentalDataSourceIdentifier: entry.dataSourceIdentitfier,
        analyteIdentifier: entry.analyte,
        valueField: 'avg',
        label: entry.name,
      })),
    },
  };
}

export const WaiLakeWahakari = ConfigureChart(
  'Analyte WAI - Field Water Sampling: Lake Wahakari',
  generateSeries(['nrc_101034'], analytesWai)
);

export const WaiLakeMorehurehu = ConfigureChart(
  'Analyte WAI - Field Water Sampling: Lake Morehurehu',
  generateSeries(['nrc_101908'], analytesWai)
);

export const WaiLakeWaihopo = ConfigureChart(
  'Analyte WAI - Field Water Sampling: Waihopo Lake',
  generateSeries(['nrc_108232', 'nrc_108232', 'nrc_108627'], analytesWai).concat(
    generateSeries(['nrc_lake_waihopo_at_sg_aupouri'], analytesWaterLevelAndTemp, { extendedName: true })
  )
);
